import React, { useEffect, useState } from "react";
import AdminLayout from "../../components/admin/AdminLayout";
import { api, money, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X } from "lucide-react";

const CARRIERS = ["usps", "ups", "fedex", "freight", "flat_rate", "local_pickup", "free"];
const CALC_MODES = ["flat", "weight", "free"];
const TABS = [
  { key: "methods", label: "Methods" },
  { key: "rates", label: "Rates" },
  { key: "classes", label: "Classes" },
];

const inputCls = "w-full h-10 px-3 bg-white border border-zinc-300 rounded text-sm focus:outline-none focus:border-zinc-900";
const label = "text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1 block";

export default function AdminShipping() {
  const [tab, setTab] = useState("methods");
  return (
    <AdminLayout title="Shipping">
      <div className="flex gap-2 mb-6" data-testid="shipping-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`shipping-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`text-sm px-4 h-9 rounded ${tab === t.key ? "bg-zinc-900 text-white" : "border border-zinc-200 hover:bg-zinc-50"}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "methods" && <MethodsTab />}
      {tab === "rates" && <RatesTab />}
      {tab === "classes" && <ClassesTab />}
    </AdminLayout>
  );
}

/* ------------------------------ METHODS ------------------------------ */
function MethodsTab() {
  const [methods, setMethods] = useState([]);
  const [classes, setClasses] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const [m, c] = await Promise.all([
        api.get("/admin/shipping-methods"),
        api.get("/admin/shipping-classes"),
      ]);
      setMethods(m.data.methods);
      setClasses(c.data.classes);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this shipping method?")) return;
    try { await api.delete(`/admin/shipping-methods/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-zinc-500">Methods valid for a cart appear at checkout. Local pickup stays hidden from customers in this phase.</p>
        <button data-testid="add-method-btn" onClick={() => setEditing({})} className="inline-flex items-center gap-2 bg-zinc-900 text-white text-sm px-4 h-9 rounded hover:bg-zinc-800"><Plus size={14} /> Add method</button>
      </div>
      <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="methods-table">
          <thead className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 bg-zinc-50">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-3 py-3">Carrier</th>
              <th className="text-left px-3 py-3">Mode</th>
              <th className="text-left px-3 py-3">Cost</th>
              <th className="text-left px-3 py-3">Visible</th>
              <th className="text-left px-3 py-3">Enabled</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {methods.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-zinc-500">No shipping methods yet.</td></tr>}
            {methods.map((m) => (
              <tr key={m.id} className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer" data-testid={`method-row-${m.id}`} onClick={() => setEditing(m)}>
                <td className="px-4 py-3 font-medium">{m.name}</td>
                <td className="px-3 py-3 uppercase text-xs">{m.carrier}</td>
                <td className="px-3 py-3 text-xs">{m.calculation_mode}</td>
                <td className="px-3 py-3 font-mono text-xs">{m.calculation_mode === "weight" ? `${money(m.per_lb_cost)}/lb` : m.calculation_mode === "free" ? "Free" : money(m.base_cost)}{m.handling_fee ? ` +${money(m.handling_fee)}` : ""}</td>
                <td className="px-3 py-3 text-xs">{m.customer_visible ? "Yes" : "No"}</td>
                <td className="px-3 py-3 text-xs">{m.enabled ? "Yes" : "No"}</td>
                <td className="px-4 py-3 text-right"><button onClick={(e) => { e.stopPropagation(); remove(m.id); }} className="text-red-600 hover:text-red-800"><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <MethodModal method={editing} classes={classes} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function MethodModal({ method, classes, onClose, onSaved }) {
  const isNew = !method.id;
  const [f, setF] = useState({
    name: method.name || "",
    carrier: method.carrier || "flat_rate",
    calculation_mode: method.calculation_mode || "flat",
    base_cost: method.base_cost ?? 0,
    per_lb_cost: method.per_lb_cost ?? 0,
    handling_fee: method.handling_fee ?? 0,
    free_over: method.free_over ?? "",
    min_weight_lbs: method.min_weight_lbs ?? "",
    max_weight_lbs: method.max_weight_lbs ?? "",
    shipping_class_ids: method.shipping_class_ids || [],
    enabled: method.enabled ?? true,
    customer_visible: method.customer_visible ?? true,
    sort_order: method.sort_order ?? 100,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const num = (v) => (v === "" || v == null ? null : Number(v));

  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: f.name.trim(),
        carrier: f.carrier,
        calculation_mode: f.calculation_mode,
        base_cost: Number(f.base_cost) || 0,
        per_lb_cost: Number(f.per_lb_cost) || 0,
        handling_fee: Number(f.handling_fee) || 0,
        free_over: num(f.free_over),
        min_weight_lbs: num(f.min_weight_lbs),
        max_weight_lbs: num(f.max_weight_lbs),
        shipping_class_ids: f.shipping_class_ids,
        enabled: !!f.enabled,
        customer_visible: !!f.customer_visible,
        sort_order: Number(f.sort_order) || 100,
      };
      if (isNew) await api.post("/admin/shipping-methods", payload);
      else await api.put(`/admin/shipping-methods/${method.id}`, payload);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const toggleClass = (id) => set("shipping_class_ids", f.shipping_class_ids.includes(id) ? f.shipping_class_ids.filter((x) => x !== id) : [...f.shipping_class_ids, id]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-md w-full max-w-lg my-8 shadow-2xl" data-testid="method-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <h2 className="font-display text-lg">{isNew ? "New shipping method" : "Edit shipping method"}</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div><label className={label}>Name</label><input data-testid="method-name" value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} placeholder="USPS Priority" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={label}>Carrier</label>
              <select data-testid="method-carrier" value={f.carrier} onChange={(e) => set("carrier", e.target.value)} className={inputCls}>
                {CARRIERS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><label className={label}>Calculation mode</label>
              <select data-testid="method-mode" value={f.calculation_mode} onChange={(e) => set("calculation_mode", e.target.value)} className={inputCls}>
                {CALC_MODES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div><label className={label}>Base cost ($)</label><input data-testid="method-base-cost" type="number" step="0.01" value={f.base_cost} onChange={(e) => set("base_cost", e.target.value)} className={inputCls} disabled={f.calculation_mode !== "flat"} /></div>
            <div><label className={label}>Per lb ($)</label><input type="number" step="0.01" value={f.per_lb_cost} onChange={(e) => set("per_lb_cost", e.target.value)} className={inputCls} disabled={f.calculation_mode !== "weight"} /></div>
            <div><label className={label}>Handling ($)</label><input type="number" step="0.01" value={f.handling_fee} onChange={(e) => set("handling_fee", e.target.value)} className={inputCls} /></div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div><label className={label}>Free over ($)</label><input type="number" step="0.01" value={f.free_over} onChange={(e) => set("free_over", e.target.value)} className={inputCls} placeholder="—" /></div>
            <div><label className={label}>Min weight (lb)</label><input type="number" step="0.01" value={f.min_weight_lbs} onChange={(e) => set("min_weight_lbs", e.target.value)} className={inputCls} placeholder="—" /></div>
            <div><label className={label}>Max weight (lb)</label><input type="number" step="0.01" value={f.max_weight_lbs} onChange={(e) => set("max_weight_lbs", e.target.value)} className={inputCls} placeholder="—" /></div>
          </div>
          {classes.length > 0 && (
            <div>
              <label className={label}>Restrict to shipping classes (optional)</label>
              <div className="flex flex-wrap gap-2">
                {classes.map((c) => (
                  <button key={c.id} type="button" onClick={() => toggleClass(c.id)} className={`text-xs px-3 py-1.5 rounded border ${f.shipping_class_ids.includes(c.id) ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 hover:border-zinc-500"}`}>{c.name}</button>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="method-enabled" checked={f.enabled} onChange={(e) => set("enabled", e.target.checked)} /> Enabled</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="method-visible" checked={f.customer_visible} onChange={(e) => set("customer_visible", e.target.checked)} /> Visible to customers</label>
            <div className="flex items-center gap-2 text-sm">Sort <input type="number" value={f.sort_order} onChange={(e) => set("sort_order", e.target.value)} className="w-16 h-8 px-2 border border-zinc-300 rounded" /></div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2">
          <button onClick={onClose} className="text-sm px-4 h-9 rounded border border-zinc-200 hover:bg-zinc-50">Cancel</button>
          <button data-testid="method-save" onClick={save} disabled={saving} className="text-sm px-5 h-9 rounded bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ RATES (reuses existing shipping_rates) ------------------------------ */
function RatesTab() {
  const [rates, setRates] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/admin/shipping-rates"); setRates(data.rates); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  const remove = async (id) => {
    if (!window.confirm("Delete this rate?")) return;
    try { await api.delete(`/admin/shipping-rates/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-zinc-500">Legacy category/weight rate rules (reused). Optional supplementary configuration.</p>
        <button data-testid="add-rate-btn" onClick={() => setEditing({})} className="inline-flex items-center gap-2 bg-zinc-900 text-white text-sm px-4 h-9 rounded hover:bg-zinc-800"><Plus size={14} /> Add rate</button>
      </div>
      <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="rates-table">
          <thead className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 bg-zinc-50">
            <tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-3 py-3">Kind</th><th className="text-left px-3 py-3">Price</th><th className="text-left px-3 py-3">Category</th><th className="text-left px-3 py-3">Active</th><th className="text-right px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {rates.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-zinc-500">No rates yet.</td></tr>}
            {rates.map((r) => (
              <tr key={r.id} className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer" data-testid={`rate-row-${r.id}`} onClick={() => setEditing(r)}>
                <td className="px-4 py-3 font-medium">{r.name}</td>
                <td className="px-3 py-3 text-xs">{r.kind}</td>
                <td className="px-3 py-3 font-mono text-xs">{money(r.price)}</td>
                <td className="px-3 py-3 text-xs">{r.applies_to_category || "all"}</td>
                <td className="px-3 py-3 text-xs">{r.active ? "Yes" : "No"}</td>
                <td className="px-4 py-3 text-right"><button onClick={(e) => { e.stopPropagation(); remove(r.id); }} className="text-red-600 hover:text-red-800"><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <RateModal rate={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function RateModal({ rate, onClose, onSaved }) {
  const isNew = !rate.id;
  const [f, setF] = useState({
    name: rate.name || "", kind: rate.kind || "flat", price: rate.price ?? 0,
    free_above: rate.free_above ?? "", applies_to_category: rate.applies_to_category || "",
    priority: rate.priority ?? 100, active: rate.active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: f.name.trim(), kind: f.kind, price: Number(f.price) || 0,
        free_above: f.free_above === "" ? null : Number(f.free_above),
        applies_to_category: f.applies_to_category || null,
        priority: Number(f.priority) || 100, active: !!f.active,
      };
      if (isNew) await api.post("/admin/shipping-rates", payload);
      else await api.put(`/admin/shipping-rates/${rate.id}`, payload);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-md w-full max-w-md my-8 shadow-2xl" data-testid="rate-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200"><h2 className="font-display text-lg">{isNew ? "New rate" : "Edit rate"}</h2><button onClick={onClose}><X size={18} /></button></div>
        <div className="px-6 py-5 space-y-4">
          <div><label className={label}>Name</label><input data-testid="rate-name" value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={label}>Kind</label>
              <select value={f.kind} onChange={(e) => set("kind", e.target.value)} className={inputCls}>
                {["flat", "per_item", "free_threshold", "free"].map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <div><label className={label}>Price ($)</label><input type="number" step="0.01" value={f.price} onChange={(e) => set("price", e.target.value)} className={inputCls} /></div>
            <div><label className={label}>Free above ($)</label><input type="number" step="0.01" value={f.free_above} onChange={(e) => set("free_above", e.target.value)} className={inputCls} placeholder="—" /></div>
            <div><label className={label}>Category</label>
              <select value={f.applies_to_category} onChange={(e) => set("applies_to_category", e.target.value)} className={inputCls}>
                <option value="">All</option><option value="side-mirror">side-mirror</option><option value="windshield">windshield</option>
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} /> Active</label>
        </div>
        <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2">
          <button onClick={onClose} className="text-sm px-4 h-9 rounded border border-zinc-200 hover:bg-zinc-50">Cancel</button>
          <button data-testid="rate-save" onClick={save} disabled={saving} className="text-sm px-5 h-9 rounded bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ CLASSES ------------------------------ */
function ClassesTab() {
  const [classes, setClasses] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/admin/shipping-classes"); setClasses(data.classes); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  const remove = async (id) => {
    if (!window.confirm("Delete this class?")) return;
    try { await api.delete(`/admin/shipping-classes/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-zinc-500">Group products (e.g. Mirror, Windshield-freight) to control method eligibility.</p>
        <button data-testid="add-class-btn" onClick={() => setEditing({})} className="inline-flex items-center gap-2 bg-zinc-900 text-white text-sm px-4 h-9 rounded hover:bg-zinc-800"><Plus size={14} /> Add class</button>
      </div>
      <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="classes-table">
          <thead className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 bg-zinc-50"><tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-3 py-3">Description</th><th className="text-right px-4 py-3"></th></tr></thead>
          <tbody>
            {classes.length === 0 && <tr><td colSpan={3} className="text-center py-8 text-zinc-500">No classes yet.</td></tr>}
            {classes.map((c) => (
              <tr key={c.id} className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer" data-testid={`class-row-${c.id}`} onClick={() => setEditing(c)}>
                <td className="px-4 py-3 font-medium">{c.name}</td>
                <td className="px-3 py-3 text-xs text-zinc-500">{c.description || "—"}</td>
                <td className="px-4 py-3 text-right"><button onClick={(e) => { e.stopPropagation(); remove(c.id); }} className="text-red-600 hover:text-red-800"><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <ClassModal cls={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ClassModal({ cls, onClose, onSaved }) {
  const isNew = !cls.id;
  const [f, setF] = useState({ name: cls.name || "", description: cls.description || "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const payload = { name: f.name.trim(), description: f.description.trim() || null };
      if (isNew) await api.post("/admin/shipping-classes", payload);
      else await api.put(`/admin/shipping-classes/${cls.id}`, payload);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-md w-full max-w-md my-8 shadow-2xl" data-testid="class-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200"><h2 className="font-display text-lg">{isNew ? "New class" : "Edit class"}</h2><button onClick={onClose}><X size={18} /></button></div>
        <div className="px-6 py-5 space-y-4">
          <div><label className={label}>Name</label><input data-testid="class-name" value={f.name} onChange={(e) => setF((s) => ({ ...s, name: e.target.value }))} className={inputCls} placeholder="Windshield (freight)" /></div>
          <div><label className={label}>Description</label><input value={f.description} onChange={(e) => setF((s) => ({ ...s, description: e.target.value }))} className={inputCls} /></div>
        </div>
        <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2">
          <button onClick={onClose} className="text-sm px-4 h-9 rounded border border-zinc-200 hover:bg-zinc-50">Cancel</button>
          <button data-testid="class-save" onClick={save} disabled={saving} className="text-sm px-5 h-9 rounded bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}
